import { Message, MessageBox } from 'element-ui'
import router from '../router'

export const AUTH_CHANGED_EVENT = 'xlb-auth-changed'

class ApiService {
    constructor() {
        this.baseUrl = 'https://pro-api.xiaobaoming.com' // 生产环境
        //this.baseUrl = 'https://pro-api2.xiaobaoming.com' // 预备环境
        //this.baseUrl = 'http://localhost:8080' // 开发环境
        this.tokenKey = 'sessionToken'
        this.isRefreshing = false
        this.refreshSubscribers = []
        // 后台定时刷新句柄
        this._autoRefreshTimer = null
        // 进入刷新窗口的阈值（毫秒）：剩余时间 <= 该值时触发刷新（与小程序 checkToken 一致：不足 1 天则刷新）
        this.refreshThresholdMs = 24 * 3600 * 1000
        // 后台轮询间隔
        this.autoRefreshIntervalMs = 30 * 60 * 1000
        this._goLoginShowing = false

        // 浏览器环境下自动启动后台刷新和可见性监听，最大化保证 token 不过期
        if (typeof window !== 'undefined') {
            this.startAutoRefresh()
            this._onVisibilityChange = () => {
                // 页面切回前台时尝试一次刷新（若 token 即将过期）
                if (document.visibilityState === 'visible') {
                    this.refreshTokenIfNeeded().catch(err => {
                        console.warn('[visibility refresh] 异常', err)
                    })
                }
            }
            document.addEventListener('visibilitychange', this._onVisibilityChange)
            // 页面加载后若已有登录态，立即检查一次是否需要续期
            if (this.getToken()) {
                this.refreshTokenIfNeeded().catch(err => {
                    console.warn('[init refresh] 异常', err)
                })
            }
        }
    }

    /**
     * 启动后台定时刷新：周期性检查 token 剩余时间，若进入刷新窗口则提前续期。
     * 没有 token 或 token 已过期时不会发起请求。
     */
    startAutoRefresh() {
        if (this._autoRefreshTimer) return
        this._autoRefreshTimer = setInterval(() => {
            this.refreshTokenIfNeeded().catch(err => {
                console.warn('[autoRefresh] 异常', err)
            })
        }, this.autoRefreshIntervalMs)
    }

    stopAutoRefresh() {
        if (this._autoRefreshTimer) {
            clearInterval(this._autoRefreshTimer)
            this._autoRefreshTimer = null
        }
    }

    getToken() {
        return localStorage.getItem(this.tokenKey)
    }

    setToken(token) {
        localStorage.setItem(this.tokenKey, token)
    }

    removeToken() {
        localStorage.removeItem(this.tokenKey)
    }

    getExpiresAt() {
        const val = localStorage.getItem('tokenExpiresAt')
        return val ? this.normalizeExpiresAt(val) : null
    }

    setExpiresAt(expiresAt) {
        const normalized = this.normalizeExpiresAt(expiresAt)
        if (normalized != null) {
            localStorage.setItem('tokenExpiresAt', String(normalized))
        }
    }

    /** 统一为毫秒时间戳（兼容服务端返回秒级时间戳） */
    normalizeExpiresAt(expiresAt) {
        const n = Number(expiresAt)
        if (!n || isNaN(n)) return null
        // 秒级时间戳约 10 位，毫秒约 13 位
        return n < 1e12 ? n * 1000 : n
    }

    // 401 时等待 token 刷新完再重试
    onRefreshed(callback) {
        this.refreshSubscribers.push(callback)
    }

    notifySubscribers(success = true) {
        const subs = this.refreshSubscribers
        this.refreshSubscribers = []
        subs.forEach(cb => {
            try { cb(success) } catch (e) { console.error('[refresh subscriber error]', e) }
        })
    }

    /** token 存在且未过期（expiresAt 缺失时视为仍可用，避免误杀老登录态） */
    hasUsableToken() {
        if (!this.getToken()) return false
        const expiresAt = this.getExpiresAt()
        if (expiresAt && !isNaN(expiresAt) && expiresAt <= Date.now()) return false
        return true
    }

    /** 只清除登录态相关的本地存储，保留 api_debug_history 等与账号无关的数据 */
    clearAuthStorage() {
        this.removeToken()
        localStorage.removeItem('tokenExpiresAt')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('expires')
        localStorage.removeItem('orgList')
        localStorage.removeItem('currentOrgId')
        localStorage.removeItem('currentOrgName')
        localStorage.removeItem('currentOrgType')
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(AUTH_CHANGED_EVENT))
        }
    }

    /** MessageBox 关闭后可能残留遮罩，挡住落地页「网页版登录」点击 */
    cleanupModalOverlay({ force = false } = {}) {
        if (typeof document === 'undefined') return
        if (!force) {
            const popupOpen = Array.from(
                document.querySelectorAll('.el-dialog__wrapper, .el-message-box__wrapper')
            ).some((el) => el.style.display !== 'none' && window.getComputedStyle(el).display !== 'none')
            if (popupOpen) return
        }
        document.querySelectorAll('body > .v-modal').forEach((el) => el.remove())
        document.body.classList.remove('el-popup-parent--hidden')
        document.body.style.removeProperty('overflow')
        document.body.style.removeProperty('padding-right')
    }

    redirectToLanding() {
        if (!router || router.currentRoute.path === '/') return
        router.replace('/').catch(() => {})
    }

    goLogin() {
        if (this._goLoginShowing) return
        this._goLoginShowing = true
        console.log('[goLogin] 被调用')
        console.trace('[goLogin] 调用栈')
        this.clearAuthStorage()
        MessageBox.alert('登录已失效，请重新登录', '提示', {
            confirmButtonText: '确认',
            closeOnClickModal: false,
            callback: () => {
                this._goLoginShowing = false
                this.cleanupModalOverlay({ force: true })
                this.redirectToLanding()
                setTimeout(() => this.cleanupModalOverlay(), 320)
            }
        })
    }

    async refreshTokenIfNeeded(force = false) {
        const token = this.getToken()
        if (!token) return false

        const expiresAt = this.getExpiresAt()
        const now = Date.now()

        // 无过期时间但有 token：强制尝试刷新以补全 expiresAt
        if (!expiresAt || isNaN(expiresAt)) {
            if (!force) return false
            console.log('[refreshToken] tokenExpiresAt 缺失，尝试刷新补全')
        } else {
            const timeLeft = expiresAt - now
            // 已过期：让上层走重新登录流程
            if (timeLeft <= 0) return false
            // 不在刷新窗口且未强制：跳过
            if (!force && timeLeft > this.refreshThresholdMs) return false
            console.log('Token还有' + (timeLeft / 1000 / 3600).toFixed(2) + '小时过期，提前刷新')
        }

        if (this.isRefreshing) {
            return new Promise(resolve => {
                this.onRefreshed(success => resolve(!!success))
            })
        }

        this.isRefreshing = true
        let success = false
        try {
            const res = await fetch(`${this.baseUrl}/api/user/refreshToken`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({})
            })
            const data = await res.json().catch(() => null)

            if (data && data.code === 200 && data.data && data.data.expiresAt) {
                this.setExpiresAt(data.data.expiresAt)
                console.log('Token刷新成功，新过期时间：' + data.data.expiresAt)
                success = true
            } else {
                console.log('刷新token失败', data)
            }
        } catch (error) {
            console.error('刷新token异常:', error)
        } finally {
            this.isRefreshing = false
            // 无论成功失败都要通知等待者，避免并发调用挂起
            this.notifySubscribers(success)
        }
        return success
    }

    async checkToken() {
        const token = this.getToken()
        const currentPath = router.currentRoute.path
        const isLoginOrRoot = currentPath === '/login' || currentPath === '/'
        console.log('[checkToken] 开始', { hasToken: !!token, currentPath })
        if (!token) {
            if (!isLoginOrRoot) {
                console.log('[checkToken] 无 token 且不在登录/根路径 -> 调用 goLogin')
                this.goLogin()
            } else {
                console.log('[checkToken] 无 token 但在登录页或根路径 -> 不弹框')
            }
            return false
        }

        console.log('[checkToken] 有 token，检查是否在有效期内')
        const expiresAt = this.getExpiresAt()

        // 无过期时间：尝试刷新补全，失败也不立刻踢出（避免老用户被误杀）
        if (!expiresAt || isNaN(expiresAt)) {
            console.log('checkToken: tokenExpiresAt 缺失，尝试刷新补全')
            await this.refreshTokenIfNeeded(true)
            return !!this.getToken()
        }

        const timeLeft = expiresAt - Date.now()

        // 已经过期：直接走登录
        if (timeLeft <= 0) {
            console.log('checkToken: token 已过期 -> goLogin')
            this.goLogin()
            return false
        }

        // 不足 1 天：尝试刷新；刷新失败但本地仍未过期则放行，由后续 API 再试
        if (timeLeft <= this.refreshThresholdMs) {
            console.log('checkToken: token 即将过期，尝试刷新')
            const ok = await this.refreshTokenIfNeeded(true)
            if (!ok) {
                console.warn('checkToken: 刷新失败，但 token 本地未过期，继续放行')
            } else {
                console.log('checkToken: token 已刷新')
            }
            return true
        }

        console.log('checkToken: token还有' + (timeLeft / 1000 / 3600 / 24).toFixed(2) + '天过期')
        return true
    }

    /**
     * 通用请求方法
     * @param {string} url - 接口路径
     * @param {string} method - HTTP 方法
     * @param {object} data - 请求体
     * @param {boolean} needAuth - 是否需要携带 token
     * @param {boolean} isRetry - 是否为重试请求
     */
    async request(url, method = 'POST', data = {}, needAuth = true, isRetry = false) {
        const headers = {
            'Content-Type': 'application/json'
        }

        if (needAuth) {
            const token = this.getToken()
            if (!token) {
                console.log('Token不存在，跳转登录')
                this.goLogin()
                return null
            }

            if (url !== '/api/user/refreshToken') {
                await this.refreshTokenIfNeeded()
                const currentToken = this.getToken() || token
                headers['Authorization'] = `Bearer ${currentToken}`
            } else {
                headers['Authorization'] = `Bearer ${token}`
            }
        }

        try {
            console.log('send request====>', `${this.baseUrl}${url}`, method, data)

            const fetchOptions = {
                method,
                headers
            }
            if (method.toUpperCase() !== 'GET') {
                fetchOptions.body = JSON.stringify(data)
            }

            const res = await fetch(`${this.baseUrl}${url}`, fetchOptions)
            const responseData = await res.json()

            console.log('response.data:', responseData)

            if (!responseData) {
                console.error('响应数据为空')
                return null
            }

            // 处理 token 过期：先尝试刷新再重试，避免网络抖动或时钟偏差直接踢下线
            if (responseData.code === 401 && needAuth) {
                console.log('[request] 收到 401', { url, isRetry })
                if (isRetry) {
                    console.log('[request] 重试仍 401 -> goLogin')
                    this.goLogin()
                    return null
                }

                if (this.isRefreshing) {
                    return new Promise(resolve => {
                        this.onRefreshed(success => {
                            if (success) {
                                resolve(this.request(url, method, data, needAuth, true))
                            } else {
                                this.goLogin()
                                resolve(null)
                            }
                        })
                    })
                }

                console.log('[request] 401，尝试强制刷新 token 后重试')
                const refreshed = await this.refreshTokenIfNeeded(true)
                if (refreshed) {
                    return this.request(url, method, data, needAuth, true)
                }
                console.log('[request] 刷新失败 -> goLogin')
                this.goLogin()
                return null
            }

            // 处理无权限
            if (responseData.code === 403) {
                Message.error(responseData.message || '无权限访问')
                return null
            }

            return responseData
        } catch (error) {
            console.error('请求失败:', error)
            if (this.isRefreshing) {
                this.isRefreshing = false
            }
            return null
        }
    }

    /**
     * 上传文件到七牛
     * @param {File[]} files - 浏览器 File 对象数组
     * @returns {Promise<Array<{url:string, size:number, mimeType:string}>>}
     */
    async uploadFiles(files) {
        const results = []
        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            let ext = 'png'
            if (file.name) {
                const lastDot = file.name.lastIndexOf('.')
                if (lastDot > -1 && lastDot < file.name.length - 1) {
                    ext = file.name.substring(lastDot + 1)
                }
            }
            const fileName = `upload_${i}.${ext}`
            const tokenRes = await this.request('/api/qiniu/upload-token', 'POST', { fileName })
            if (!tokenRes || !tokenRes.data) {
                throw new Error('获取上传凭证失败')
            }

            const formData = new FormData()
            formData.append('file', file)
            formData.append('token', tokenRes.data.uploadToken)
            formData.append('key', tokenRes.data.fileKey)
            formData.append('x:uid', tokenRes.data['x:uid'])
            formData.append('x:fileName', tokenRes.data['x:fileName'])

            const uploadRes = await fetch('https://up-z2.qiniup.com', {
                method: 'POST',
                body: formData
            })
            const uploadData = await uploadRes.json()

            if (uploadData && uploadData.data) {
                results.push({
                    url: uploadData.data.fileUrl,
                    size: uploadData.data.fileSize,
                    mimeType: uploadData.data.mimeType
                })
            } else {
                throw new Error('上传无返回')
            }
        }
        return results
    }

    /**
     * 批量为列表中的每条记录装饰 user 的 nickName、avatarUrl
     * @param {Array} userList - 每项需包含 userIdKey 指定的用户ID字段
     * @param {string} userIdKey - 用户ID在列表项中的字段名，默认 'userId'
     * @returns {Promise<Array>} 原列表（已原地写入 nickName、avatarUrl）
     */
    async decorateProfile(userList, userIdKey = 'userId') {
        if (!userList || userList.length === 0) return userList
        const userIds = [...new Set(userList.map(x => x[userIdKey]).filter(Boolean))]
        if (userIds.length === 0) return userList
        const res = await this.request('/api/user/batchProfile', 'POST', { userIds }, true)
        const userDict = res && res.code === 200 && res.data ? res.data : {}
        for (const item of userList) {
            const profile = userDict[item[userIdKey]]
            if (profile) {
                item.nickName = profile.nickName
                item.avatarUrl = profile.avatarUrl
                if (profile.lcUserId != null) {
                    item.lcUserId = profile.lcUserId
                }
                if (profile.subscribeOpenId != null) {
                    item.subscribeOpenId = profile.subscribeOpenId
                }
            }
        }
        return userList
    }

    /**
     * 批量为列表中的每条活动装饰 UV（浏览人数）
     * @param {Array} keventList - 每项需包含 keventIdKey 指定的活动ID字段
     * @param {string} keventIdKey - 活动ID在列表项中的字段名，默认 'id'
     * @returns {Promise<Array>} 原列表（已原地写入 UV）
     */
    async decorateKeventUv(keventList, keventIdKey = 'id') {
        if (!keventList || keventList.length === 0) return keventList
        const keventIds = [...new Set(keventList.map(x => x[keventIdKey]).filter(Boolean))]
        if (keventIds.length === 0) return keventList

        const uvDict = {}
        const chunkSize = 100
        for (let i = 0; i < keventIds.length; i += chunkSize) {
            const chunk = keventIds.slice(i, i + chunkSize)
            const res = await this.request('/api/kevent-log/batchUv', 'POST', { keventIds: chunk }, true)
            if (res && res.code === 200 && res.data) {
                Object.assign(uvDict, res.data)
            }
        }
        for (const item of keventList) {
            const brief = uvDict[item[keventIdKey]]
            if (brief && brief.uv != null) {
                item.UV = brief.uv
            }
        }
        return keventList
    }
}

export default new ApiService()
